import { SearchInput } from "@similarweb/ui-components";
import * as _ from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { colorsPalettes } from "@similarweb/styles";
import { defaultGetStableKey } from "app/components/React/Table/SWReactTable";
import { i18nFilter } from "app/filters/i18nFilter";
import { Pagination } from "@similarweb/ui-components";
import { segmentUserFlowBucketTableColumnsConfigGen } from "app/pages/segment-user-flow/buyer-journey/SegmentUserFlowBucketTableColumnsConfig";
import { SWReactTableOptimized } from "app/components/React/Table/SWReactTableOptimized";
import type { IInteraction } from "app/pages/segment-user-flow/buyer-journey/SegmentUserFlowContainer";

const TableWrapper = styled.div<{ loading: boolean }>`
    pointer-events: ${({ loading }) => (loading ? "none" : "all")};
    width: 100%;
    max-width: 1368px;
    height: auto;
    display: block;
`;

const SearchContainer = styled.div`
    border-top: 1px solid ${colorsPalettes.carbon[100]};
    .SearchInput-container .SearchInput {
        height: 34px;
        background-color: ${colorsPalettes.carbon[0]};
        border: none;
        width: 100%;
        box-sizing: border-box;
        padding: 9px 2px 5px 50px;
        box-shadow: none;
        margin-bottom: 0px;
        &:focus {
            box-shadow: none !important;
            border: none;
        }
    }
`;

const StyledPaginationWrapper = styled.div`
    padding-top: 18px;
    padding-bottom: 18px;
`;

interface ISegmentUserFlowTableContainerProps {
    tableData: any;
    selectedInteraction?: IInteraction;
    isLoading?: boolean;
}

const ITEMS_PER_PAGE = 100;

const SegmentUserFlowBucketTable = (props: ISegmentUserFlowTableContainerProps) => {
    const [searchString, setSearchString] = useState<string>("");
    const [page, setPage] = useState<number>(1);
    const [itemsCount, setItemsCount] = useState<number>(1);
    const [isLoadingFilteredData, setIsLoadingFilteredData] = useState<boolean>(true);
    const [sortedColumn, setSortedColumn] = useState<{
        field: string;
        sortDirection: "desc" | "asc";
    }>({
        field: "Visits",
        sortDirection: "desc",
    });
    const [filteredData, setFilteredData] = useState([]);
    const { isLoading, selectedInteraction } = props;

    const getSearchFilteredData = () => {
        const { tableData } = props;
        const lowertSearchString = searchString.toLowerCase();

        return _.filter(tableData, (dataItem: any) => {
            return (
                dataItem.From.toLowerCase().includes(lowertSearchString) ||
                dataItem.To.toLowerCase().includes(lowertSearchString)
            );
        });
    };

    // handle filtering and pagination:
    const setTableFilters = useCallback(
        (page = 1) => {
            const { tableData } = props;
            if (!isLoading && tableData) {
                const maxPages = Math.ceil(tableData.length / ITEMS_PER_PAGE);
                const paginationDataContainer = new Array(maxPages);
                let slicedPosition = [0, ITEMS_PER_PAGE];

                let newFilteredData = searchString === "" ? tableData : getSearchFilteredData();
                const [filteredGraphData, filteredDisabledData] = _.partition(
                    newFilteredData,
                    (data) => !data.isDisabled,
                );

                newFilteredData = [
                    ..._.orderBy(
                        filteredGraphData,
                        (data) => data[sortedColumn.field] || "",
                        sortedColumn.sortDirection,
                    ),
                    ...filteredDisabledData,
                ];

                if (maxPages === 1) {
                    setFilteredData(newFilteredData);
                    setItemsCount(newFilteredData.length);
                    setIsLoadingFilteredData(false);
                    return;
                }

                // case that we need to create pagination:
                for (let i = 0; i < paginationDataContainer.length; i++) {
                    paginationDataContainer[i] = newFilteredData.slice(slicedPosition[0], slicedPosition[1]);
                    if (slicedPosition[1] + ITEMS_PER_PAGE <= newFilteredData.length) {
                        slicedPosition = [slicedPosition[0] + ITEMS_PER_PAGE, slicedPosition[1] + ITEMS_PER_PAGE];
                    } else {
                        slicedPosition = [slicedPosition[0] + ITEMS_PER_PAGE, newFilteredData.length];
                    }
                }
                setPage(page);
                setFilteredData(paginationDataContainer[page - 1]);
                setItemsCount(newFilteredData.length);
                setIsLoadingFilteredData(false);
            }
        },
        [isLoading, props.tableData, searchString, sortedColumn],
    );

    useEffect(() => {
        setTableFilters(page);
    }, [searchString, props.tableData, sortedColumn]);

    const onSearch = (val) => {
        setSearchString(val.trim());
    };

    const onSort = ({ field, sortDirection }) => {
        setSortedColumn({
            field,
            sortDirection,
        });
    };

    const getStableKey = (index, col, row) => {
        if (row) {
            const { From, To } = row;
            return `${From}_${To}`;
        }
        return defaultGetStableKey(index, col);
    };

    const tableProps = useMemo(() => {
        return {
            onSort: onSort,
            isLoading: isLoadingFilteredData,
            tableData: { Data: filteredData, page: page },
            tableColumns: segmentUserFlowBucketTableColumnsConfigGen(
                sortedColumn.field,
                sortedColumn.sortDirection,
                selectedInteraction,
            ),
            getStableKey: getStableKey,
        };
    }, [getStableKey, props.isLoading, onSort, filteredData, sortedColumn, selectedInteraction]);

    return (
        <TableWrapper loading={isLoadingFilteredData}>
            <>
                <SearchContainer>
                    <SearchInput
                        defaultValue={searchString}
                        debounce={100}
                        onChange={onSearch}
                        placeholder={i18nFilter()("buyer.journey.bucket.table.search.input.placeholder.text")}
                    />
                </SearchContainer>

                <SWReactTableOptimized
                    {...tableProps}
                    isLoading={isLoadingFilteredData}
                />

                <StyledPaginationWrapper>
                    <Pagination
                        handlePageChange={setTableFilters}
                        captionPosition={"center"}
                        page={page}
                        itemsCount={itemsCount}
                        itemsPerPage={ITEMS_PER_PAGE}
                        hasItemsPerPageSelect={false}
                    />
                </StyledPaginationWrapper>
            </>
        </TableWrapper>
    );
};

export default SegmentUserFlowBucketTable;
